/**
 * Fit heuristics for the activity picker: given where a slot sits in the
 * day's computed schedule, which options plainly don't align? Pure functions
 * — everything they know arrives as arguments, so the tests need no DOM.
 *
 * These are BADGES, not gates: a warned card stays choosable. The schedule
 * engine remains the authority on actual times; these only pre-read the
 * obvious collisions (an option that can't fit before the ferry, a place
 * that opens after you leave, a spot that's closed by the time you'd arrive).
 */
import type { DayComputation, FixedRowComp, SlotRowComp } from "@trip/shared/engine/schedule";
import { fmin, pmin } from "@trip/shared/engine/time";
import { isFixed, type Act, type DayDef } from "@trip/shared/types";

/** "3h 40m" */
const hm = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m}m`;
};

export interface SlotFit {
  /** when this slot would start (null: skipped/blank — no modeled start) */
  startMin: number | null;
  /** minutes available before the next hard commitment (null: unknowable) */
  freeMin: number | null;
  /** the next fixed item's clock time after this slot, if any */
  anchorMin: number | null;
  /** what the free time runs up against, already formatted ("6:00 PM ferry" → "6:00 PM") */
  until: string | null;
}

/**
 * Where a slot sits relative to the day's commitments. Definition slots read
 * their own computed row and scan forward for the next fixed anchor; extra
 * rows ride after every definition item, so their anchor comes from the
 * items themselves (the engine's `extra` describes only the blank row).
 */
export function slotFit(day: DayDef, comp: DayComputation, slotIndex: number | string, dayEndMin: number | null): SlotFit {
  const isExtra = typeof slotIndex === "string";

  const row = (
    isExtra ? comp.rows.find((r) => r?.kind === "slot" && r.i === slotIndex) : comp.rows[slotIndex]
  ) as SlotRowComp | undefined;
  const startMin = row && row.kind === "slot" && !row.skipped ? row.startMin : null;

  // the next fixed commitment: after this item for definition slots, after
  // the last flexible item for extras (that's where extra rows mount)
  let anchorMin: number | null = null;
  if (isExtra) {
    const lastFlex = day.items.reduce((acc, it, i) => (isFixed(it) ? acc : i), -1);
    for (let j = lastFlex + 1; j < day.items.length; j++) {
      const it = day.items[j];
      if (isFixed(it) && it.t) {
        anchorMin = pmin(it.t);
        break;
      }
    }
  } else if (typeof slotIndex === "number") {
    for (let j = slotIndex + 1; j < comp.rows.length; j++) {
      const r = comp.rows[j];
      if (r?.kind === "fixed") {
        anchorMin = (r as FixedRowComp).tMin;
        break;
      }
    }
  }

  // free time before the anchor — or before the back-at-base target when the
  // day has no later fixed item. Blank extras have no start; the engine's
  // extra.slackMin covers them at the call site.
  let freeMin: number | null = null;
  let until: string | null = null;
  if (startMin !== null && anchorMin !== null) {
    freeMin = anchorMin - startMin;
    until = `your ${fmin(anchorMin % 1440)} departure`;
  } else if (startMin !== null && dayEndMin !== null) {
    freeMin = dayEndMin - startMin;
    until = "your back-by target";
  }
  return { startMin, freeMin, anchorMin, until };
}

/** First "HH:MM-HH:MM" range in an hours string; overnight closes wrap past 24h. */
export function parseHours(hours?: string): { open: number; close: number } | null {
  const m = (hours ?? "").match(/(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const open = Number(m[1]) * 60 + Number(m[2]);
  let close = Number(m[3]) * 60 + Number(m[4]);
  if (close <= open) close += 1440;
  return { open, close };
}

/**
 * Up to two plain-language badges for one option in one slot. `travelMin` is
 * the modeled travel cost into the option (null when unpinned).
 */
export function fitWarnings(act: Act, travelMin: number | null, fit: SlotFit): string[] {
  const warns: string[] = [];
  const minDur = act.durs.length ? Math.min(...act.durs.filter((d) => d > 0)) : 1;
  const need = (travelMin ?? 0) + minDur * 60;
  if (fit.freeMin !== null && fit.freeMin > 0 && need > fit.freeMin) {
    warns.push(`⚠ Tight fit — ${hm(fit.freeMin)} before ${fit.until ?? "day's end"}, needs ~${hm(need)}`);
  }

  const hrs = parseHours(act.hours);
  if (hrs) {
    if (fit.anchorMin !== null && hrs.open >= fit.anchorMin) {
      // the evening-only spot on a morning-departure day
      warns.push(`⚠ Opens ${fmin(hrs.open)} — after you leave`);
    } else if (fit.startMin !== null && fit.startMin + (travelMin ?? 0) >= hrs.close) {
      warns.push(`⚠ Likely closed by the time you'd arrive`);
    }
  }
  return warns.slice(0, 2);
}
